function isValidPhoneNumber(value) {
  if (!/^[0-9+\-\s()]+$/.test(value)) return false;
  const digitCount = (value.match(/\d/g) || []).length;
  return digitCount >= 7 && digitCount <= 15;
}

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer")) this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        const phoneInput = this.form.querySelector('[name="properties[Phone Number]"]');
        if (phoneInput) {
          phoneInput.addEventListener("input", () => {
            if (isValidPhoneNumber(phoneInput.value.trim())) {
              phoneInput.removeAttribute("aria-invalid");
              this.handleErrorMessage();
            }
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // The phone field lives once on the page (main product form); other
        // add-to-cart entry points (e.g. the sticky bar) fall back to it.
        const phoneInput =
          this.form.querySelector('[name="properties[Phone Number]"]') ||
          document.querySelector('[name="properties[Phone Number]"]');
        const phoneValue = phoneInput ? phoneInput.value.trim() : "";
        // The sticky bar's own product-form has no error banner of its own,
        // so always report through the instance that actually owns the field.
        const phoneFormInstance = phoneInput ? phoneInput.closest("product-form") || this : this;

        if (phoneInput && !phoneValue) {
          phoneFormInstance.handleErrorMessage("Please enter your phone number to continue.");
          phoneInput.setAttribute("aria-invalid", "true");
          phoneInput.scrollIntoView({ behavior: "smooth", block: "center" });
          phoneInput.focus();
          return;
        }
        if (phoneInput && !isValidPhoneNumber(phoneValue)) {
          phoneFormInstance.handleErrorMessage("Please enter a valid phone number.");
          phoneInput.setAttribute("aria-invalid", "true");
          phoneInput.scrollIntoView({ behavior: "smooth", block: "center" });
          phoneInput.focus();
          return;
        }
        if (phoneInput) {
          phoneInput.removeAttribute("aria-invalid");
          phoneFormInstance.handleErrorMessage();
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (phoneInput) formData.set("properties[Phone Number]", phoneValue);

        // Add selected products from the upsell component, if present
        const upsellItems = this.getUpsellProducts();
        if (upsellItems.length > 0) {
          upsellItems.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            if (phoneInput) formData.append(`items[${index}][properties][Phone Number]`, phoneValue);
          });
        }

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker("add:wait-for-subscribers");
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker("add:wait-for-subscribers", startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty")) this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      // Collects selected products from the upsell component if present
      getUpsellProducts() {
        // First we search in the current container
        const productInfoContainer = this.closest(".product__info-container");
        let selectedCheckboxes = [];
        if (productInfoContainer) {
          const upsellBlock = productInfoContainer.querySelector(".component-products-upsell");
          if (upsellBlock) {
            selectedCheckboxes = upsellBlock.querySelectorAll('input[type="checkbox"]:checked');
          }
        }
        // If you haven’t found it, look globally
        if (!selectedCheckboxes || selectedCheckboxes.length === 0) {
          selectedCheckboxes = document.querySelectorAll('.component-products-upsell input[type="checkbox"]:checked');
        }
        if (!selectedCheckboxes || selectedCheckboxes.length === 0) return [];
        return Array.from(selectedCheckboxes).map((checkbox) => ({
          id: checkbox.dataset.variantId,
          quantity: 1,
        }));
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
